/**
 * House Mapping Utilities v2.0 - Enhanced and Optimized
 * North Indian Chart Layout mappings with performance optimizations
 * 
 * REFACTORED: Consolidated duplicate mapping logic and added caching
 * OPTIMIZED: High-performance house and rasi mapping utilities
 * PERFORMANCE: Added Map-based lookups for O(1) performance
 */

import { RasiName } from '../../../../../models/types';

/**
 * ENHANCED: Centralized Rasi Data with comprehensive mapping information
 * All rasi-related data consolidated for performance and maintainability
 */
interface RasiInfo {
    containerIndex: number;  // 0-11 for North Indian layout
    houseNumber: number;     // 1-12 traditional house numbering
    rasiIndex: number;       // 0-11 for longitude calculations
    englishName: string;     // English zodiac names
    symbol: string;          // Unicode zodiac symbols
    element: string;         // Fire, Earth, Air, Water
    quality: string;         // Cardinal, Fixed, Mutable
}

/**
 * CONSOLIDATED: All rasi information in single source of truth
 * PERFORMANCE: O(1) lookups using Map instead of multiple Record objects
 */
const RASI_INFO_MAP = new Map<RasiName, RasiInfo>([
    [RasiName.Mesha, {
        containerIndex: 1, houseNumber: 1, rasiIndex: 0,
        englishName: 'Mesha', symbol: '♈', element: 'Fire', quality: 'Cardinal'
    }],
    [RasiName.Vrishabha, {
        containerIndex: 2, houseNumber: 2, rasiIndex: 1,
        englishName: 'Vrishabha', symbol: '♉', element: 'Earth', quality: 'Fixed'
    }],
    [RasiName.Mithuna, {
        containerIndex: 3, houseNumber: 3, rasiIndex: 2,
        englishName: 'Mithuna', symbol: '♊', element: 'Air', quality: 'Mutable'
    }],
    [RasiName.Karka, {
        containerIndex: 4, houseNumber: 4, rasiIndex: 3,
        englishName: 'Karka', symbol: '♋', element: 'Water', quality: 'Cardinal'
    }],
    [RasiName.Simha, {
        containerIndex: 5, houseNumber: 5, rasiIndex: 4,
        englishName: 'Simha', symbol: '♌', element: 'Fire', quality: 'Fixed'
    }],
    [RasiName.Kanya, {
        containerIndex: 6, houseNumber: 6, rasiIndex: 5,
        englishName: 'Kanya', symbol: '♍', element: 'Earth', quality: 'Mutable'
    }],
    [RasiName.Tula, {
        containerIndex: 7, houseNumber: 7, rasiIndex: 6,
        englishName: 'Tula', symbol: '♎', element: 'Air', quality: 'Cardinal'
    }],
    [RasiName.Vrishchika, {
        containerIndex: 8, houseNumber: 8, rasiIndex: 7,
        englishName: 'Vrishchika', symbol: '♏', element: 'Water', quality: 'Fixed'
    }],
    [RasiName.Dhanu, {
        containerIndex: 9, houseNumber: 9, rasiIndex: 8,
        englishName: 'Dhanu', symbol: '♐', element: 'Fire', quality: 'Mutable'
    }],
    [RasiName.Makara, {
        containerIndex: 10, houseNumber: 10, rasiIndex: 9,
        englishName: 'Makara', symbol: '♑', element: 'Earth', quality: 'Cardinal'
    }],
    [RasiName.Kumbha, {
        containerIndex: 11, houseNumber: 11, rasiIndex: 10,
        englishName: 'Kumbha', symbol: '♒', element: 'Air', quality: 'Fixed'
    }],
    [RasiName.Meena, {
        containerIndex: 0, houseNumber: 12, rasiIndex: 11,
        englishName: 'Meena', symbol: '♓', element: 'Water', quality: 'Mutable'
    }]
]);

/**
 * BACKWARD COMPATIBILITY: Legacy mapping object (derived from central data)
 * This maintains existing API while using optimized internal structure
 */
export const RASI_CONTAINER_MAPPING: Record<RasiName, number> = 
    Object.fromEntries(
        Array.from(RASI_INFO_MAP.entries()).map(([rasi, info]) => [rasi, info.containerIndex])
    ) as Record<RasiName, number>;

// ========== OPTIMIZED CORE FUNCTIONS (BACKWARD COMPATIBLE) ==========

/**
 * Get container ID for planet based on rasi
 * OPTIMIZED: O(1) lookup using Map
 */
export function getContainerIdForRasi(rasi: RasiName): string {
    const info = RASI_INFO_MAP.get(rasi);
    return `container-${info?.containerIndex ?? 1}`;
}

/**
 * Get rasi number for display (1-12)
 * OPTIMIZED: Direct calculation without lookup
 */
export function getRasiNumber(containerIndex: number): number {
    // Container 0 = House 12, Container 1 = House 1, ..., Container 11 = House 11
    return containerIndex === 0 ? 12 : containerIndex;
}

/**
 * Get rasi index (0-11) from RasiName for longitude calculations
 * OPTIMIZED: O(1) lookup using Map
 */
export function getRasiIndex(rasi: RasiName): number {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.rasiIndex ?? 0;
}

/**
 * Get container index from rasi (for new architecture)
 * OPTIMIZED: O(1) lookup using Map
 */
export function getContainerIndexForRasi(rasi: RasiName): number {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.containerIndex ?? 1;
}

/**
 * Get house number from rasi (1-12)
 * OPTIMIZED: O(1) lookup using Map
 */
export function getHouseNumberFromRasi(rasi: RasiName): number {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.houseNumber ?? 1;
}

// ========== BACKWARD COMPATIBILITY FUNCTIONS ==========

/**
 * String to RasiName conversion with performance optimization
 * ENHANCED: Added caching for repeated conversions
 */
const _stringToRasiCache = new Map<string, RasiName>();

export function stringToRasiName(rasiString: string): RasiName {
    // Check cache first
    if (_stringToRasiCache.has(rasiString)) {
        return _stringToRasiCache.get(rasiString)!;
    }
    
    const mapping: Record<string, RasiName> = {
        'Mesha': RasiName.Mesha,
        'Vrishabha': RasiName.Vrishabha,
        'Mithuna': RasiName.Mithuna,
        'Karka': RasiName.Karka,
        'Simha': RasiName.Simha,
        'Kanya': RasiName.Kanya,
        'Tula': RasiName.Tula,
        'Vrischika': RasiName.Vrishchika, // Note: Vrischika maps to Vrishchika
        'Vrishchika': RasiName.Vrishchika,
        'Dhanu': RasiName.Dhanu,
        'Makara': RasiName.Makara,
        'Kumbha': RasiName.Kumbha,
        'Meena': RasiName.Meena
    };
    
    const result = mapping[rasiString] || RasiName.Mesha;
    _stringToRasiCache.set(rasiString, result);
    return result;
}

/**
 * Get house number from rasi string (high-performance lookup)
 * ENHANCED: Uses central data source instead of duplicate mapping
 */
export function getHouseNumberFromRasiString(rasiString: string): number {
    const rasiName = stringToRasiName(rasiString);
    return getHouseNumberFromRasi(rasiName);
}

// ========== NEW ENHANCED FUNCTIONS ==========

/**
 * NEW: Get complete rasi information in single lookup
 */
export function getRasiInfo(rasi: RasiName): RasiInfo | null {
    return RASI_INFO_MAP.get(rasi) || null;
}

/**
 * NEW: Get English zodiac name
 */
export function getRasiEnglishName(rasi: RasiName): string {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.englishName ?? 'Unknown';
}

/**
 * NEW: Get zodiac symbol (Unicode)
 */
export function getRasiSymbol(rasi: RasiName): string {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.symbol ?? '?';
}

/**
 * NEW: Get element (Fire, Earth, Air, Water)
 */
export function getRasiElement(rasi: RasiName): string {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.element ?? 'Unknown';
}

/**
 * NEW: Get quality (Cardinal, Fixed, Mutable)
 */
export function getRasiQuality(rasi: RasiName): string {
    const info = RASI_INFO_MAP.get(rasi);
    return info?.quality ?? 'Unknown';
}

/**
 * NEW: Get rasi from container index (reverse lookup)
 */
export function getRasiFromContainerIndex(containerIndex: number): RasiName | null {
    for (const [rasi, info] of RASI_INFO_MAP.entries()) {
        if (info.containerIndex === containerIndex) {
            return rasi;
        }
    }
    return null;
}

/**
 * NEW: Get rasi from house number (reverse lookup)
 */
export function getRasiFromHouseNumber(houseNumber: number): RasiName | null {
    for (const [rasi, info] of RASI_INFO_MAP.entries()) {
        if (info.houseNumber === houseNumber) {
            return rasi;
        }
    }
    return null;
}

/**
 * NEW: Validate rasi-related mappings for consistency
 */
export function validateRasiMappings(): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
} {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Check for duplicate container indices
    const containerIndices = Array.from(RASI_INFO_MAP.values()).map(info => info.containerIndex);
    const duplicateContainers = containerIndices.filter((index, pos) => containerIndices.indexOf(index) !== pos);
    if (duplicateContainers.length > 0) {
        errors.push(`Duplicate container indices: ${duplicateContainers.join(', ')}`);
    }
    
    // Check for duplicate house numbers
    const houseNumbers = Array.from(RASI_INFO_MAP.values()).map(info => info.houseNumber);
    const duplicateHouses = houseNumbers.filter((num, pos) => houseNumbers.indexOf(num) !== pos);
    if (duplicateHouses.length > 0) {
        errors.push(`Duplicate house numbers: ${duplicateHouses.join(', ')}`);
    }
    
    // Check for missing indices/numbers
    for (let i = 0; i < 12; i++) {
        if (!containerIndices.includes(i)) {
            warnings.push(`Missing container index: ${i}`);
        }
        if (!houseNumbers.includes(i + 1)) {
            warnings.push(`Missing house number: ${i + 1}`);
        }
    }
    
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}

/**
 * NEW: Performance utilities
 */
export function clearHouseMappingCache(): void {
    _stringToRasiCache.clear();
}

export function getHouseMappingCacheStats(): {
    stringToRasiCacheSize: number;
    totalMappings: number;
} {
    return {
        stringToRasiCacheSize: _stringToRasiCache.size,
        totalMappings: RASI_INFO_MAP.size
    };
}

/**
 * NEW: Get all rasi names as array (useful for iterations)
 */
export function getAllRasiNames(): RasiName[] {
    return Array.from(RASI_INFO_MAP.keys());
}

/**
 * NEW: Get rasi positions in traditional order (Mesha first)
 */
export function getRasiInTraditionalOrder(): RasiName[] {
    const ordered = Array.from(RASI_INFO_MAP.entries())
        .sort(([, a], [, b]) => a.rasiIndex - b.rasiIndex)
        .map(([rasi]) => rasi);
    return ordered;
}

// Export for browser console debugging
if (typeof window !== 'undefined') {
    (window as any).HouseMappingUtils = {
        getRasiInfo,
        validateRasiMappings,
        clearCache: clearHouseMappingCache,
        getCacheStats: getHouseMappingCacheStats,
        getAllRasiNames,
        getRasiInTraditionalOrder,
        RASI_INFO_MAP: () => RASI_INFO_MAP
    };
}